import { isMock, db } from './firebase'
import { logger } from './logger'
import { getUserProfile } from './userService'
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  increment,
  DocumentData,
  Timestamp
} from 'firebase/firestore'
import type { Listing, PropertyListing, CarListing, ServiceListing, GenericListing, ListingFilters } from '../types/listing'

// Spam detection configuration
const SPAM_SIMILARITY_THRESHOLD = 0.8 // 80% similarity considered spam
const SPAM_TIME_WINDOW_HOURS = 24 // Check last 24 hours
const MAX_SIMILAR_LISTINGS = 3 // Maximum similar listings allowed in time window

// Helper function to calculate text similarity (Jaccard index)
const calculateTextSimilarity = (text1: string, text2: string): number => {
  if (!text1 || !text2) return 0
  
  // Normalize text: lowercase, remove punctuation, split into words
  const normalize = (text: string): string[] => {
    return text.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2) // ignore short words
  }
  
  const words1 = normalize(text1)
  const words2 = normalize(text2)
  
  if (words1.length === 0 || words2.length === 0) return 0
  
  // Calculate Jaccard similarity: intersection / union
  const set1 = new Set(words1)
  const set2 = new Set(words2)
  
  const intersection = [...set1].filter(word => set2.has(word)).length
  const union = new Set([...words1, ...words2]).size
  
  return union > 0 ? intersection / union : 0
}

// Helper function to check for spam by similarity
const checkSpamSimilarity = async (userId: string, title: string, description: string): Promise<boolean> => {
  if (isMock) {
    // In mock mode, allow all listings
    return false
  }

  try {
    const listingsRef = collection(db, LISTINGS_COLLECTION)
    const now = new Date()
    const timeWindow = new Date(now.getTime() - SPAM_TIME_WINDOW_HOURS * 60 * 60 * 1000)
    
    // Query recent listings by same user
    const q = query(
      listingsRef,
      where('userId', '==', userId),
      where('createdAt', '>=', timeWindow),
      orderBy('createdAt', 'desc')
    )
    
    const snapshot = await getDocs(q)
    const recentListings: Listing[] = []
    snapshot.forEach((doc) => {
      recentListings.push(firestoreToListing(doc.id, doc.data()))
    })
    
    // Check similarity with each recent listing
    let similarCount = 0
    for (const listing of recentListings) {
      const titleSimilarity = calculateTextSimilarity(title, listing.title)
      const descSimilarity = calculateTextSimilarity(description, listing.description)
      const maxSimilarity = Math.max(titleSimilarity, descSimilarity)
      
      if (maxSimilarity >= SPAM_SIMILARITY_THRESHOLD) {
        similarCount++
        if (similarCount >= MAX_SIMILAR_LISTINGS) {
          logger.warn(`Spam detected: user ${userId} has ${similarCount} similar listings in last ${SPAM_TIME_WINDOW_HOURS} hours`)
          return true
        }
      }
    }
    
    return false
  } catch (error) {
    logger.error('Error checking spam similarity:', error)
    // If there's an error, allow the listing (fail-safe)
    return false
  }
}

// Helper function to check for duplicate images
const checkDuplicateImages = async (userId: string, images: string[]): Promise<boolean> => {
  if (isMock || !images || images.length === 0) {
    return false
  }

  try {
    const listingsRef = collection(db, LISTINGS_COLLECTION)
    const now = new Date()
    const timeWindow = new Date(now.getTime() - SPAM_TIME_WINDOW_HOURS * 60 * 60 * 1000)
    
    // Query recent listings by same user
    const q = query(
      listingsRef,
      where('userId', '==', userId),
      where('createdAt', '>=', timeWindow),
      orderBy('createdAt', 'desc')
    )
    
    const snapshot = await getDocs(q)
    const recentListings: Listing[] = []
    snapshot.forEach((doc) => {
      recentListings.push(firestoreToListing(doc.id, doc.data()))
    })
    
    // Check for duplicate images
    for (const listing of recentListings) {
      const listingImages = listing.images || []
      for (const img of images) {
        if (listingImages.includes(img)) {
          logger.warn(`Duplicate image detected: user ${userId} reused image ${img}`)
          return true
        }
      }
    }
    
    return false
  } catch (error) {
    logger.error('Error checking duplicate images:', error)
    return false
  }
}

// Firestore collections
const LISTINGS_COLLECTION = 'listings'
const LIKES_COLLECTION = 'listingLikes'

// Helper to remove undefined values from Firestore data
const sanitizeFirestoreData = (data: DocumentData): DocumentData => {
  const sanitized: DocumentData = {}
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      sanitized[key] = value
    }
  }
  return sanitized
}

// Helper function to check if a document listing is expired
const isDocumentExpired = (listing: Listing): boolean => {
  if (listing.type === 'generic' && listing.mainCategory === 'documents') {
    const generic = listing as GenericListing
    const expiryStr = generic.customFields?.documentExpiry as string | undefined
    if (expiryStr) {
      try {
        const expiryDate = new Date(expiryStr)
        const now = new Date()
        // Consider expired if expiry date is before today (ignoring time)
        return expiryDate < now
      } catch (e) {
        console.error('Invalid expiry date:', expiryStr, e)
        return false
      }
    }
  }
  return false
}

// Helper function to convert Firestore data to Listing
const firestoreToListing = (docId: string, data: DocumentData): Listing => {
  // Convert Firestore Timestamp to Date
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()
  
  // Debug logging
  logger.log('Firestore listing data:', { docId, images: data.images, title: data.title })

  const baseListing = {
    id: docId,
    title: data.title || '',
    description: data.description || '',
    price: data.price || 0,
    priceUnit: data.priceUnit || 'USD',
    location: data.location || '',
    city: data.city || '',
    state: data.state || '',
    images: data.images || [],
    userId: data.userId || '',
    userName: data.userName || '',
    userAvatar: data.userAvatar || '',
    contactPhone: data.contactPhone || '',
    contactWhatsApp: data.contactWhatsApp || '',
    status: data.status || 'pending',
    approvalStatus: data.approvalStatus || 'pending',
    isPaid: data.isPaid || false,
    paidExpiry: data.paidExpiry ? convertTimestamp(data.paidExpiry) : undefined,
    featuredLevel: data.featuredLevel || 'none',
    mainCategory: data.mainCategory || 'others',
    subCategory: data.subCategory || undefined,
    transactionType: data.transactionType || undefined,
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    views: data.views || 0,
    likes: data.likes || 0,
  }

  // Add type-specific fields
  switch (data.type) {
    case 'property':
      return {
        ...baseListing,
        type: 'property',
        propertyType: data.propertyType || 'house',
        bedrooms: data.bedrooms || 0,
        bathrooms: data.bathrooms || 0,
        area: data.area || 0,
        garageSpots: data.garageSpots || 0,
        amenities: data.amenities || [],
        address: data.address || '',
        zipCode: data.zipCode || '',
      } as PropertyListing
    case 'car':
      return {
        ...baseListing,
        type: 'car',
        make: data.make || '',
        model: data.model || '',
        year: data.year || 0,
        mileage: data.mileage || 0,
        fuelType: data.fuelType || 'gasoline',
        transmission: data.transmission || 'automatic',
        color: data.color || '',
        plate: data.plate || '',
        condition: data.condition || 'used',
        features: data.features || [],
      } as CarListing
    case 'service':
      return {
        ...baseListing,
        type: 'service',
        category: data.category || 'outro',
        duration: data.duration || '',
        providerExperience: data.providerExperience || 0,
        availability: data.availability || 'flexible',
        serviceArea: data.serviceArea || [],
        certifications: data.certifications || [],
      } as ServiceListing
    case 'generic':
      return {
        ...baseListing,
        type: 'generic',
        mainCategory: data.mainCategory || 'others',
        subCategory: data.subCategory || undefined,
        customFields: data.customFields || {},
      } as GenericListing
    default:
      throw new Error(`Unknown listing type: ${data.type}`)
  }
}

export const getMonthlyRevenue = async (): Promise<number> => {
  if (isMock) {
    logger.log('Mock: getting monthly revenue')
    // Random revenue between R$ 1,000 and R$ 10,000 for mock
    return Math.floor(Math.random() * 9000) + 1000
  }

  try {
    const listingsRef = collection(db, LISTINGS_COLLECTION)
    const now = new Date()
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const q = query(
      listingsRef,
      where('isPaid', '==', true),
      where('approvalStatus', '==', 'approved'),
      where('createdAt', '>=', firstDayOfMonth),
      where('createdAt', '<=', now)
    )
    const snapshot = await getDocs(q)
    // Assume each paid listing contributes R$ 50 (could be configurable later)
    const revenuePerListing = 50
    return snapshot.size * revenuePerListing
  } catch (error) {
    logger.error('Error fetching monthly revenue from Firestore:', error)
    return 0
  }
}

// Helper function to convert Listing to Firestore data
const listingToFirestore = (listing: Omit<Listing, 'id' | 'createdAt' | 'updatedAt' | 'views' | 'likes'>) => {
  const baseData: DocumentData = {}
  
  // Helper to add field if value is not undefined
  const addIfDefined = (key: string, value: unknown) => {
    if (value !== undefined) {
      baseData[key] = value as DocumentData[string]
    }
  }
  
  // Add required fields (should never be undefined)
  baseData.title = listing.title
  baseData.description = listing.description
  baseData.price = listing.price
  addIfDefined('priceUnit', listing.priceUnit)
  baseData.location = listing.location
  baseData.city = listing.city
  baseData.state = listing.state
  baseData.images = listing.images
  baseData.userId = listing.userId
  baseData.userName = listing.userName
  addIfDefined('userAvatar', listing.userAvatar)
  baseData.contactPhone = listing.contactPhone
  baseData.contactWhatsApp = listing.contactWhatsApp
  baseData.status = listing.status
  baseData.approvalStatus = listing.approvalStatus
  baseData.isPaid = listing.isPaid
  baseData.featuredLevel = listing.featuredLevel
  // Always save mainCategory with default 'others' if undefined
  baseData.mainCategory = listing.mainCategory || 'others'
  addIfDefined('subCategory', listing.subCategory)
  addIfDefined('transactionType', listing.transactionType)
  baseData.views = 0
  baseData.likes = 0
  baseData.createdAt = serverTimestamp()
  baseData.updatedAt = serverTimestamp()
  
  addIfDefined('paidExpiry', listing.paidExpiry)

  // Add type-specific fields with type assertions
  if (listing.type === 'property') {
    const propertyListing = listing as PropertyListing
    baseData.type = 'property'
    baseData.propertyType = propertyListing.propertyType
    baseData.bedrooms = propertyListing.bedrooms
    baseData.bathrooms = propertyListing.bathrooms
    baseData.area = propertyListing.area
    baseData.garageSpots = propertyListing.garageSpots
    baseData.amenities = propertyListing.amenities
    baseData.address = propertyListing.address
    baseData.zipCode = propertyListing.zipCode
  } else if (listing.type === 'car') {
    const carListing = listing as CarListing
    baseData.type = 'car'
    baseData.make = carListing.make
    baseData.model = carListing.model
    baseData.year = carListing.year
    baseData.mileage = carListing.mileage
    baseData.fuelType = carListing.fuelType
    baseData.transmission = carListing.transmission
    baseData.color = carListing.color
    addIfDefined('plate', carListing.plate)
    baseData.condition = carListing.condition
    baseData.features = carListing.features
  } else if (listing.type === 'service') {
    const serviceListing = listing as ServiceListing
    baseData.type = 'service'
    baseData.category = serviceListing.category
    baseData.duration = serviceListing.duration
    baseData.providerExperience = serviceListing.providerExperience
    baseData.availability = serviceListing.availability
    baseData.serviceArea = serviceListing.serviceArea
    baseData.certifications = serviceListing.certifications
  } else if (listing.type === 'generic') {
    const genericListing = listing as GenericListing
    baseData.type = 'generic'
    baseData.mainCategory = genericListing.mainCategory
    addIfDefined('subCategory', genericListing.subCategory)
    addIfDefined('customFields', genericListing.customFields)
  }

  return baseData
}

// Mock data generation
const generateMockListings = (): Listing[] => {
  const properties: PropertyListing[] = [
    {
      id: 'prop-1',
      type: 'property',
      title: 'Casa com 3 quartos em Orlando',
      description: 'Excelente casa localizada em comunidade tranquila, próxima a escolas e shopping.',
      price: 350000,
      priceUnit: 'USD',
      location: 'Orlando, FL',
      city: 'Orlando',
      state: 'FL',
      images: ['https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'],
      userId: 'user-1',
      userName: 'João Silva',
      userAvatar: 'https://i.pravatar.cc/150?u=user-1',
      contactPhone: '+15551234567',
      contactWhatsApp: '+15551234567',
      status: 'active',
      approvalStatus: 'approved',
      isPaid: false,
      featuredLevel: 'none',
        mainCategory: 'housing',
        subCategory: 'outro',
        transactionType: 'sale',
       createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-03-10'),
      views: 1245,
      likes: 89,
      propertyType: 'house',
      bedrooms: 3,
      bathrooms: 2,
      area: 180,
      garageSpots: 2,
      amenities: ['piscina', 'ar condicionado', 'garagem'],
      address: '123 Main St',
      zipCode: '32801',
    },
    {
      id: 'prop-2',
      type: 'property',
      title: 'Apartamento moderno em Miami',
      description: 'Apartamento totalmente reformado com vista para o mar, ótima localização.',
      price: 550000,
      priceUnit: 'USD',
      location: 'Miami, FL',
      city: 'Miami',
      state: 'FL',
      images: ['https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w-800'],
      userId: 'user-2',
      userName: 'Maria Souza',
      userAvatar: 'https://i.pravatar.cc/150?u=user-2',
      contactPhone: '+15552345678',
      contactWhatsApp: '+15552345678',
      status: 'active',
      approvalStatus: 'approved',
      isPaid: true,
      featuredLevel: 'premium',
        mainCategory: 'housing',
        subCategory: 'outro',
        transactionType: 'rent',
       createdAt: new Date('2024-02-20'),
      updatedAt: new Date('2024-03-12'),
      views: 890,
      likes: 45,
      propertyType: 'apartment',
      bedrooms: 2,
      bathrooms: 2,
      area: 110,
      garageSpots: 1,
      amenities: ['academia', 'segurança 24h', 'varanda'],
      address: '456 Ocean Dr',
      zipCode: '33139',
    },
  ]

  const cars: CarListing[] = [
    {
      id: 'car-1',
      type: 'car',
      title: 'Toyota Camry 2022',
      description: 'Carro em excelente estado, único dono, sempre na concessionária.',
      price: 25000,
      priceUnit: 'USD',
      location: 'Houston, TX',
      city: 'Houston',
      state: 'TX',
      images: ['https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800'],
      userId: 'user-3',
      userName: 'Carlos Mendes',
      userAvatar: 'https://i.pravatar.cc/150?u=user-3',
      contactPhone: '+15553456789',
      contactWhatsApp: '+15553456789',
      status: 'active',
      approvalStatus: 'approved',
      isPaid: false,
       featuredLevel: 'none',
       mainCategory: 'cars',
       subCategory: 'outro',
       createdAt: new Date('2024-03-01'),
      updatedAt: new Date('2024-03-15'),
      views: 543,
      likes: 32,
      make: 'Toyota',
      model: 'Camry',
      year: 2022,
      mileage: 18000,
      fuelType: 'hybrid',
      transmission: 'automatic',
      color: 'prata',
      condition: 'used',
      features: ['câmera de ré', 'piloto automático', 'teto solar'],
    },
    {
      id: 'car-2',
      type: 'car',
      title: 'Honda Civic 2020',
      description: 'Carro conservado, baixa quilometragem, pronto para uso.',
      price: 19000,
      priceUnit: 'USD',
      location: 'Atlanta, GA',
      city: 'Atlanta',
      state: 'GA',
      images: ['https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=800'],
      userId: 'user-4',
      userName: 'Ana Costa',
      userAvatar: 'https://i.pravatar.cc/150?u=user-4',
      contactPhone: '+15554567890',
      contactWhatsApp: '+15554567890',
      status: 'active',
      approvalStatus: 'approved',
       isPaid: true,
       featuredLevel: 'standard',
       mainCategory: 'cars',
       subCategory: 'outro',
       createdAt: new Date('2024-02-28'),
      updatedAt: new Date('2024-03-14'),
      views: 421,
      likes: 28,
      make: 'Honda',
      model: 'Civic',
      year: 2020,
      mileage: 35000,
      fuelType: 'gasoline',
      transmission: 'automatic',
      color: 'preto',
      condition: 'used',
      features: ['banco de couro', 'entrada USB', 'sensor de estacionamento'],
    },
  ]

  const services: ServiceListing[] = [
    {
      id: 'serv-1',
      type: 'service',
      title: 'Encanador experiente',
      description: 'Serviços de encanamento residencial e comercial, urgências atendidas.',
      price: 80,
      priceUnit: 'USD',
      location: 'New York, NY',
      city: 'New York',
      state: 'NY',
      images: ['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800'],
      userId: 'user-5',
      userName: 'Roberto Lima',
      userAvatar: 'https://i.pravatar.cc/150?u=user-5',
      contactPhone: '+15555678901',
      contactWhatsApp: '+15555678901',
      status: 'active',
      approvalStatus: 'approved',
       isPaid: false,
       featuredLevel: 'none',
       mainCategory: 'services',
       subCategory: 'outro',
       createdAt: new Date('2024-03-05'),
      updatedAt: new Date('2024-03-18'),
      views: 312,
      likes: 15,
      category: 'reparo',
      duration: '2 hours',
      providerExperience: 12,
      availability: 'flexible',
      serviceArea: ['Manhattan', 'Brooklyn', 'Queens'],
      certifications: ['Licença NYC', 'Certificação EPA'],
    },
    {
      id: 'serv-2',
      type: 'service',
      title: 'Limpeza residencial',
      description: 'Limpeza completa de casas e apartamentos, produtos ecológicos.',
      price: 120,
      priceUnit: 'USD',
      location: 'Los Angeles, CA',
      city: 'Los Angeles',
      state: 'CA',
      images: ['https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800'],
      userId: 'user-6',
      userName: 'Fernanda Oliveira',
      userAvatar: 'https://i.pravatar.cc/150?u=user-6',
      contactPhone: '+15556789012',
      contactWhatsApp: '+15556789012',
      status: 'active',
      approvalStatus: 'approved',
       isPaid: true,
       featuredLevel: 'premium',
       mainCategory: 'services',
       subCategory: 'outro',
       createdAt: new Date('2024-03-10'),
      updatedAt: new Date('2024-03-16'),
      views: 278,
      likes: 21,
      category: 'limpeza',
      duration: '4 hours',
      providerExperience: 8,
      availability: 'weekdays',
      serviceArea: ['LA County', 'Orange County'],
      certifications: ['Certificação Green Clean'],
    },
  ]

  return [...properties, ...cars, ...services]
}

// Service functions
export const listingService = {
   async getListings(filters: ListingFilters = {}): Promise<Listing[]> {
    logger.log('getListings called with filters:', filters)
    if (isMock) {
      logger.log('Using mock mode')
      const mockListings = generateMockListings()
      logger.log('Total mock listings:', mockListings.length)
      // Simple filtering (for demo)
      const filtered = mockListings.filter(listing => {
        if (filters.type && listing.type !== filters.type) return false
        if (filters.minPrice && listing.price < filters.minPrice) return false
        if (filters.maxPrice && listing.price > filters.maxPrice) return false
        if (filters.location && !listing.location.toLowerCase().includes(filters.location.toLowerCase())) return false
        if (filters.status && listing.status !== filters.status) return false
        if (filters.approvalStatus && listing.approvalStatus !== filters.approvalStatus) return false
        if (filters.isPaid !== undefined && listing.isPaid !== filters.isPaid) return false
        if (filters.featuredLevel && listing.featuredLevel !== filters.featuredLevel) return false
        if (filters.mainCategory && listing.mainCategory !== filters.mainCategory) {
          logger.log(`Listing ${listing.id} mainCategory ${listing.mainCategory} does not match filter ${filters.mainCategory}`)
          return false
        }
        if (filters.subCategory && listing.subCategory !== filters.subCategory) return false
        if (filters.transactionType && listing.transactionType !== filters.transactionType) return false
        if (isDocumentExpired(listing)) return false
        return true
      })
      logger.log('Filtered listings count:', filtered.length)
      return filtered
    }

    try {
      const listingsRef = collection(db, LISTINGS_COLLECTION)
      let q = query(listingsRef, where('status', '==', 'active'))

      // Apply filters
      if (filters.type) {
        q = query(q, where('type', '==', filters.type))
      }
      if (filters.minPrice !== undefined) {
        q = query(q, where('price', '>=', filters.minPrice))
      }
      if (filters.maxPrice !== undefined) {
        q = query(q, where('price', '<=', filters.maxPrice))
      }
      if (filters.location) {
        // For location filtering, we could use Firestore's array-contains or 
        // full-text search (would require a separate search index)
        // For now, we'll filter client-side for location
        q = query(q)
      }
      if (filters.status) {
        q = query(q, where('status', '==', filters.status))
      }
      if (filters.approvalStatus) {
        q = query(q, where('approvalStatus', '==', filters.approvalStatus))
      }
      if (filters.isPaid !== undefined) {
        q = query(q, where('isPaid', '==', filters.isPaid))
      }
      if (filters.featuredLevel) {
        q = query(q, where('featuredLevel', '==', filters.featuredLevel))
      }
      if (filters.mainCategory) {
        q = query(q, where('mainCategory', '==', filters.mainCategory))
      }
      if (filters.subCategory) {
        q = query(q, where('subCategory', '==', filters.subCategory))
      }
      if (filters.transactionType) {
        q = query(q, where('transactionType', '==', filters.transactionType))
      }

      // Order by creation date (newest first)
      q = query(q, orderBy('createdAt', 'desc'))

      const querySnapshot = await getDocs(q)
      const listings: Listing[] = []

      querySnapshot.forEach((doc) => {
        const listing = firestoreToListing(doc.id, doc.data())
        // Apply location filter client-side if needed
        if (filters.location && !listing.location.toLowerCase().includes(filters.location.toLowerCase())) {
          return
        }
        listings.push(listing)
      })

      // Filter out expired documents
      return listings.filter(listing => !isDocumentExpired(listing))
    } catch (error) {
      logger.error('Error fetching listings from Firestore:', error)
      return []
    }
  },

  async getListingById(id: string): Promise<Listing | null> {
    if (isMock) {
      const mockListings = generateMockListings()
      return mockListings.find(listing => listing.id === id) || null
    }

    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, id)
      const listingDoc = await getDoc(listingRef)
      
      if (!listingDoc.exists()) {
        return null
      }
      
      return firestoreToListing(listingDoc.id, listingDoc.data())
    } catch (error) {
      logger.error('Error fetching listing from Firestore:', error)
      return null
    }
  },

  async createListing(listing: Omit<Listing, 'id' | 'createdAt' | 'updatedAt' | 'views' | 'likes'>): Promise<string> {
    logger.log('createListing called, isMock:', isMock, 'db available:', !!db, 'listing type:', listing.type)
    logger.log('Listing data:', {
      title: listing.title,
      price: listing.price,
      imagesCount: listing.images?.length || 0,
      userId: listing.userId,
      status: listing.status
    })
    
    if (isMock) {
      logger.log('Mock: creating listing', listing)
      logger.log('Returning mock ID')
      return 'mock-id-' + Date.now()
    }

    // Check for spam similarity and duplicate images
    const isSpam = await checkSpamSimilarity(listing.userId, listing.title, listing.description)
    const hasDuplicateImages = await checkDuplicateImages(listing.userId, listing.images || [])
    
    if (isSpam) {
      throw new Error('Spam detected: Your listing is too similar to previous listings you posted recently. Please modify the title or description.')
    }
    
    if (hasDuplicateImages) {
      throw new Error('Spam detected: You are reusing images from previous listings. Please upload new images.')
    }

    try {
      const firestoreData = listingToFirestore(listing)
      logger.log('Firestore data prepared:', firestoreData)
      const listingsRef = collection(db, LISTINGS_COLLECTION)
      logger.log('Adding document to collection:', LISTINGS_COLLECTION)
      const docRef = await addDoc(listingsRef, firestoreData)
      logger.log('Document created with ID:', docRef.id)
      return docRef.id
    } catch (error) {
      logger.error('Error creating listing in Firestore:', error)
      logger.error('Error details:', error instanceof Error ? error.message : String(error))
      logger.error('Stack trace:', error instanceof Error ? error.stack : 'No stack')
      // If error is due to missing permissions or network, fallback to mock mode?
      throw error
    }
  },

  async updateListing(id: string, updates: Partial<Listing>): Promise<void> {
    if (isMock) {
      logger.log('Mock: updating listing', id, updates)
      return
    }

    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, id)
      const updateData: DocumentData = sanitizeFirestoreData({
        ...updates,
        updatedAt: serverTimestamp(),
      })
      // Remove fields that shouldn't be updated
      delete updateData.id
      delete updateData.createdAt
      delete updateData.views
      delete updateData.likes
      
      await updateDoc(listingRef, updateData)
    } catch (error) {
      logger.error('Error updating listing in Firestore:', error)
      throw error
    }
  },

  async deleteListing(id: string, userId?: string): Promise<void> {
    if (isMock) {
      logger.log('Mock: deleting listing', id, 'by user', userId)
      return
    }

    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, id)
      
      // If userId is provided, check permissions
      if (userId) {
        const listingDoc = await getDoc(listingRef)
        if (!listingDoc.exists()) {
          throw new Error('Listing not found')
        }
        const listingData = listingDoc.data()
        const listingUserId = listingData.userId
        
        // Check if user is the owner
        if (listingUserId !== userId) {
          // Check if user is global admin
          const userProfile = await getUserProfile(userId)
          const isAdmin = userProfile?.isAdmin || userProfile?.role === 'admin'
          if (!isAdmin) {
            throw new Error('You do not have permission to delete this listing')
          }
        }
      }
      
      await deleteDoc(listingRef)
    } catch (error) {
      logger.error('Error deleting listing from Firestore:', error)
      throw error
    }
  },

  async toggleLike(listingId: string, userId: string): Promise<number> {
    if (isMock) {
      logger.log('Mock: toggle like', listingId, userId)
      // In real app, would update likes count
      return 0
    }

    try {
      const likeRef = doc(db, LIKES_COLLECTION, `${listingId}_${userId}`)
      const likeDoc = await getDoc(likeRef)
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId)
      
      if (likeDoc.exists()) {
        // User already liked, remove like
        await deleteDoc(likeRef)
        await updateDoc(listingRef, {
          likes: increment(-1),
          updatedAt: serverTimestamp(),
        })
        
        const listingDoc = await getDoc(listingRef)
        return listingDoc.data()?.likes || 0
      } else {
        // Add like
        await setDoc(likeRef, {
          listingId,
          userId,
          createdAt: serverTimestamp(),
        })
        await updateDoc(listingRef, {
          likes: increment(1),
          updatedAt: serverTimestamp(),
        })
        
        const listingDoc = await getDoc(listingRef)
        return listingDoc.data()?.likes || 0
      }
    } catch (error) {
      logger.error('Error toggling like in Firestore:', error)
      return 0
    }
  },

  async incrementViews(listingId: string): Promise<void> {
    if (isMock) {
      logger.log('Mock: increment views', listingId)
      return
    }

    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId)
      await updateDoc(listingRef, {
        views: increment(1),
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      logger.error('Error incrementing views in Firestore:', error)
    }
  },

  async getPendingListings(): Promise<Listing[]> {
    if (isMock) {
      const mockListings = generateMockListings()
      return mockListings.filter(listing => listing.approvalStatus === 'pending')
    }

    try {
      const listingsRef = collection(db, LISTINGS_COLLECTION)
      const q = query(listingsRef, where('approvalStatus', '==', 'pending'), orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      const listings: Listing[] = []
      querySnapshot.forEach((doc) => {
        listings.push(firestoreToListing(doc.id, doc.data()))
      })
      return listings
    } catch (error) {
      logger.error('Error fetching pending listings from Firestore:', error)
      return []
    }
  },

  async approveListing(listingId: string): Promise<void> {
    if (isMock) {
      logger.log('Mock: approving listing', listingId)
      return
    }

    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId)
      await updateDoc(listingRef, {
        approvalStatus: 'approved',
        status: 'active',
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      logger.error('Error approving listing in Firestore:', error)
      throw error
    }
  },

  async rejectListing(listingId: string): Promise<void> {
    if (isMock) {
      logger.log('Mock: rejecting listing', listingId)
      return
    }

    try {
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId)
      await updateDoc(listingRef, {
        approvalStatus: 'rejected',
        status: 'inactive',
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      logger.error('Error rejecting listing in Firestore:', error)
      throw error
    }
  },

  async getActivePropertiesCount(): Promise<number> {
    logger.log('getActivePropertiesCount - isMock:', isMock)
    if (isMock) {
      logger.log('Returning mock count: 156')
      return 156
    }

    try {
      logger.log('Fetching real properties count from Firestore...')
      const listingsRef = collection(db, LISTINGS_COLLECTION)
      const q = query(listingsRef, 
        where('type', '==', 'property'),
        where('status', '==', 'active'),
        where('approvalStatus', '==', 'approved')
      )
      const snapshot = await getDocs(q)
      logger.log('Firestore properties count:', snapshot.size)
      return snapshot.size
    } catch (error) {
      logger.error('Error getting active properties count from Firestore:', error)
      return 0
    }
  },

  async getActiveCarsCount(): Promise<number> {
    logger.log('getActiveCarsCount - isMock:', isMock)
    if (isMock) {
      logger.log('Returning mock count: 89')
      return 89
    }

    try {
      logger.log('Fetching real cars count from Firestore...')
      const listingsRef = collection(db, LISTINGS_COLLECTION)
      const q = query(listingsRef, 
        where('type', '==', 'car'),
        where('status', '==', 'active'),
        where('approvalStatus', '==', 'approved')
      )
      const snapshot = await getDocs(q)
      logger.log('Firestore cars count:', snapshot.size)
      return snapshot.size
    } catch (error) {
      logger.error('Error getting active cars count from Firestore:', error)
      return 0
    }
  },

  async getActiveServicesCount(): Promise<number> {
    logger.log('getActiveServicesCount - isMock:', isMock)
    if (isMock) {
      logger.log('Returning mock count: 73')
      return 73
    }

    try {
      logger.log('Fetching real services count from Firestore...')
      const listingsRef = collection(db, LISTINGS_COLLECTION)
      const q = query(listingsRef, 
        where('type', '==', 'service'),
        where('status', '==', 'active'),
        where('approvalStatus', '==', 'approved')
      )
      const snapshot = await getDocs(q)
      logger.log('Firestore services count:', snapshot.size)
      return snapshot.size
    } catch (error) {
      logger.error('Error getting active services count from Firestore:', error)
      return 0
    }
  },
}